import React, { useRef, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import ContactForm from "./ContactForm"
import AppShowcase from "./AppShowCase"
import "./about.css"

const PLAYSTORE_URL = "https://play.google.com/store/apps/details?id=com.jamaisvuca.justnow"
const APPSTORE_URL = "https://apps.apple.com/us/app/justnow/id1561946002?l=fr&ls=1"
const GOOGLE_STORE_IMG = "https://justnow.app/img/gl.png"
const APPLE_STORE_IMG = "https://justnow.app/img/ap.png"
const PRO_VIDEO_URL = "/video/pro_demo.mp4"
const QR = "https://justnow.app/img/qr_justnow.png"
/*
 * ─── Images du Store ───
 * Ajoute ici les chemins vers tes captures d'écran du store.
 * Elles seront affichées bord à bord dans le carrousel.
 *
 * Option 1 — fichiers dans /public/img/ :
 *   const STORE_IMAGES = ["/img/store1.png", "/img/store2.png", ...]
 *
 * Option 2 — imports depuis src/assets/ :
 *   import s1 from "../../assets/store1.png"
 *   const STORE_IMAGES = [s1, s2, s3, ...]
 */
const STORE_IMAGES = [
    "/img/store/store_screen_1.png",
    "/img/store/store_screen_2.png",
    "/img/store/store_screen_3.png",
    "/img/store/store_screen_4.png",
    "/img/store/store_screen_5.png",
    "/img/store/store_screen_6.png",
    "/img/store/store_screen_7.png",
]

const FeatureCard = ({ icon, title, description }) => (
    <div className="lp-feature-card">
        <div className="lp-feature-icon">{icon}</div>
        <h3 className="lp-feature-title">{title}</h3>
        <p className="lp-feature-desc">{description}</p>
    </div>
)

const StepCard = ({ number, title, description }) => (
    <div className="lp-step-card">
        <span className="lp-step-number">{number}</span>
        <h3 className="lp-step-title">{title}</h3>
        <p className="lp-step-desc">{description}</p>
    </div>
)

const AboutPage = () => {
    const videoRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(false)

    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    video.play().then(() => {
                        setIsPlaying(true)
                    }).catch(() => {
                        setIsPlaying(false)
                    })
                } else {
                    video.pause()
                    setIsPlaying(false)
                }
            },
            { threshold: 0.4 }
        )

        observer.observe(video)
        return () => observer.disconnect()
    }, [])

    const togglePlay = () => {
        const video = videoRef.current
        if (!video) return
        if (video.paused) {
            video.play()
            setIsPlaying(true)
        } else {
            video.pause()
            setIsPlaying(false)
        }
    }

    return (
        <div className="landing-page">

            {/* ━━━ Hero ━━━ */}
            <section className="lp-hero">
                <div className="lp-hero-bg" />
                <div className="lp-hero-content">
                    <span className="lp-hero-badge">Disponible sur iOS & Android</span>
                    <h1 className="lp-hero-title">
                        Découvrez tout ce qui
                        <br />
                        <span className="lp-hero-accent">se passe autour de vous</span>
                    </h1>
                    <p className="lp-hero-subtitle">
                        JustNow vous géolocalise et affiche en temps réel les événements,
                        bons plans et commerces de proximité à portée de main.
                        Sans inscription, sans recherche — tout est déjà là.
                    </p>

                    <div className="lp-hero-actions">
                        <a
                            href="https://justnow.app/redirect"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={QR}
                                alt="Télécharger"
                                height={220}
                                width={220}
                            />
                        </a>
                    </div>
                    <div className="lp-hero-actions">
                        <a
                            href={PLAYSTORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lp-store-badge"
                        >
                            <img
                                src={GOOGLE_STORE_IMG}
                                alt="Télécharger sur Google Play"
                            />
                        </a>
                        <a
                            href={APPSTORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lp-store-badge"
                        >
                            <img
                                src={APPLE_STORE_IMG}
                                alt="Télécharger sur l'App Store"
                            />
                        </a>
                    </div>
                    <div className="lp-hero-stats">
                        <div className="lp-stat">
                            <strong>Gratuit</strong>
                            <span>pour tous</span>
                        </div>
                        <div className="lp-stat-divider" />
                        <div className="lp-stat">
                            <strong>Temps réel</strong>
                            <span>géolocalisation</span>
                        </div>
                        <div className="lp-stat-divider" />
                        <div className="lp-stat">
                            <strong>pas d'inscription obligatoire</strong>
                            <span>accès instantané</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ━━━ App Screenshots Showcase ━━━ */}
            <AppShowcase images={STORE_IMAGES} />

            {/* ━━━ Features ━━━ */}
            <section className="lp-features" id="features">
                <div className="lp-section-inner">
                    <span className="lp-section-label">Ce que vous trouverez</span>
                    <h2 className="lp-section-title">Votre quartier en un coup d'œil</h2>
                    <p className="lp-section-subtitle">
                        Découvertes Shopping, Promo Flash, Offres boutiques exclusives,
                        Restaurants tendance… JustNow agrège tout ce qui mérite votre attention, à proximité.
                    </p>
                    <div className="lp-features-grid">
                        <FeatureCard
                            icon="🛍"
                            title="Commerces de proximité"
                            description="Retrouvez les offres et promotions exclusives des boutiques et commerces de votre quartier, mises à jour en continu."
                        />
                        <FeatureCard
                            icon="🍽"
                            title="Bars & Restaurants"
                            description="Menus du jour, happy hours, événements spéciaux — ne manquez plus aucune bonne affaire gourmande autour de vous."
                        />
                        <FeatureCard
                            icon="🎭"
                            title="Culture & Sorties"
                            description="Concerts, spectacles, expositions, en partenariat avec les plus grands réseaux de billetterie. La culture à portée de main."
                        />
                        <FeatureCard
                            icon="📍"
                            title="Géolocalisation précise"
                            description="Pas besoin de chercher, JustNow détecte votre position et affiche instantanément ce qui se passe dans votre périmètre."
                        />
                    </div>
                </div>
            </section>

            {/* ━━━ How it works ━━━ */}
            <section className="lp-howitworks">
                <div className="lp-section-inner">
                    <span className="lp-section-label">Simple comme bonjour</span>
                    <h2 className="lp-section-title">Comment ça marche ?</h2>
                    <div className="lp-steps-grid">
                        <StepCard
                            number="01"
                            title="Téléchargez l'application"
                            description="Disponible gratuitement sur Google Play et l'App Store. L'installation prend quelques secondes."
                        />
                        <StepCard
                            number="02"
                            title="Activez la géolocalisation"
                            description="JustNow utilise votre position pour afficher les événements et offres les plus proches de vous."
                        />
                        <StepCard
                            number="03"
                            title="Explorez votre environnement"
                            description="Parcourez le flux en temps réel : bons plans, sorties, nouveautés… tout votre quartier à portée de main."
                        />
                    </div>
                </div>
            </section>

            {/* ━━━ Pro section ━━━ */}
            <section className="lp-pro-section">
                <div className="lp-section-inner">
                    <div className="lp-pro-content">
                        <span className="lp-section-label lp-label-light">Espace Professionnel</span>
                        <h2 className="lp-pro-title">
                            Commerçants, restaurateurs,
                            <br />
                            donnez de la visibilité à votre activité
                        </h2>
                        <p className="lp-pro-text">
                            Créez votre compte Professionnel directement depuis l'application
                            et commencez à publier vos événements, offres et promotions
                            auprès de milliers d'utilisateurs géolocalisés autour de votre commerce.
                        </p>
                        <div className="lp-pro-actions">
                            <Link to="/moveon" className="lp-btn lp-btn-light">
                                Créer un compte Pro
                            </Link>
                            <a href="#section_contact" className="lp-btn lp-btn-outline-light">
                                Nous contacter
                            </a>
                        </div>
                    </div>

                    {/* ─── Vidéo démo Pro ─── */}
                    <div className="lp-pro-video-wrapper">
                        <video
                            ref={videoRef}
                            className="lp-pro-video"
                            src={PRO_VIDEO_URL}
                            // loop
                            // muted
                            playsInline
                            preload="metadata"
                        />
                        <button
                            className={`lp-pro-video-btn ${isPlaying ? "lp-pro-video-btn--playing" : ""}`}
                            onClick={togglePlay}
                            aria-label={isPlaying ? "Mettre en pause" : "Lire la vidéo"}
                        >
                            {isPlaying ? (
                                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28a1 1 0 0 0-1.5.86z" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </section>

            {/* ━━━ Social ━━━ */}
            <section className="lp-social-strip">
                <div className="lp-section-inner lp-social-inner">
                    <span>Suivez-nous sur les réseaux</span>
                    <a
                        href="https://www.facebook.com/ekrakcom-1032858806762550/?fref=ts"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-social-link lp-social-link--facebook"
                    >
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                        Facebook
                    </a>

                    <a href="https://www.instagram.com/justnoweurope/" className="lp-social-link lp-social-link--instagram" target="_blank" rel="noopener noreferrer">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                        </svg>
                        Instagram
                    </a>

                    <a href="https://www.tiktok.com/@justnoweurope" className="lp-social-link lp-social-link--tiktok" target="_blank" rel="noopener noreferrer">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                        </svg>
                        TikTok
                    </a>

                    <a href="https://www.linkedin.com/company/justnow-europe/" className="lp-social-link  lp-social-link--linkedin" target="_blank" rel="noopener noreferrer">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        LinkedIn
                    </a>


                </div>
            </section>

            {/* ━━━ Contact Form ━━━ */}
            <ContactForm />

            {/* ━━━ Footer ━━━ */}
            <footer className="lp-footer">
                <div className="lp-footer-inner">
                    <div className="lp-footer-brand">
                        <img
                            src="https://justnow.app/logo.png"
                            alt="JustNow"
                            className="lp-footer-logo"
                        />
                        <p className="lp-footer-tagline">
                            À vos côtés, où que vous soyez.
                        </p>
                    </div>

                    <div className="lp-footer-links">
                        <Link to="/">Accueil</Link>
                        <a href="#section_contact">Contact</a>
                        <a
                            href="https://justnow.app/legal/cgv.html"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Mentions légales
                        </a>
                        <a
                            href="https://justnow.app/legal/cgu.html"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            CGU
                        </a>
                    </div>

                    <div className="lp-footer-legal">
                        <p>Google Play™ et le logo Google Play sont des marques de Google Inc.</p>
                        <p>Apple, App Store™ et le logo Apple sont des marques d'Apple Inc.</p>
                        <p>© {new Date().getFullYear()} JustNow SARL — 7B Mte du Lutetia, 06500 Menton</p>
                        <p>SIRET : 51787172900011</p>
                    </div>
                </div>
            </footer>
        </div>
    )
}

export default AboutPage

